/**
 * Pure reduction of one batch's failures, kept free of store/network imports so
 * the standalone test runner can exercise it (UploadManager pulls in the whole
 * store/network graph).
 */
import type {
  AddMediaViaUrlNotification,
  FailedUpload,
  UploadFileNotification,
} from '@stores/Notifications';
import type { Media } from '@typings/Media';
import type { UploadFailureCategory } from './uploadFailure';

export type RejectedFile = {
  file: File;
  reason: string;
  /** Stable category for UI routing — match on this, not reason substrings. */
  category: UploadFailureCategory;
  /** Batch notification this rejection belongs to. Undefined for library-scoped
   * rejects (drag-drop validation rejects when no batch was created). */
  notificationId?: string;
  /** 'failed' (error during upload/validation) or 'cancelled' (user aborted).
   * Undefined means 'failed' for backward compatibility with code that doesn't
   * set it. */
  status?: 'failed' | 'cancelled';
  /** The backend media a failed upload created before it failed. Left `empty`,
   * it is hidden from every library view, so this row is the only handle on it. */
  createdMedia?: { libraryId: string; mediaId: string };
};

export type BatchFailures = {
  uploadFailures: FailedUpload[];
  pendingUploadFailures: Array<Pick<RejectedFile, 'file' | 'reason' | 'category' | 'createdMedia'>>;
  processingFailures: Array<{ mediaId: string; media?: Media; reason: string }>;
  cancelled: Array<{ file: File; reason: string }>;
  succeededCount: number;
};

/** See `UploadManager.getBatchFailures`, which passes its `rejectedFiles`. */
export function summarizeBatchFailures(
  notification: UploadFileNotification | AddMediaViaUrlNotification,
  rejectedFiles: RejectedFile[],
  folderFiles?: Media[],
): BatchFailures {
  const notificationId = notification.id;

  // URL imports fail before their batch exists, so they never record one.
  const uploadFailures = notification.type === 'upload-file' ? notification.failedUploads : [];
  const tagged = rejectedFiles.filter((r) => r.notificationId === notificationId);
  const pendingUploadFailures = tagged
    .filter((r) => (r.status ?? 'failed') === 'failed')
    .map((r) => ({
      file: r.file,
      reason: r.reason,
      category: r.category,
      createdMedia: r.createdMedia,
    }));
  const cancelled = tagged
    .filter((r) => r.status === 'cancelled')
    .map((r) => ({ file: r.file, reason: r.reason }));

  const mediaById = new Map<string, Media>();
  if (folderFiles) {
    for (const f of folderFiles) mediaById.set(f._id, f);
  }
  const processingFailures = notification.erroredMediaIds.map((mediaId) => ({
    mediaId,
    media: mediaById.get(mediaId),
    reason: 'Processing failed',
  }));

  const succeededCount = Math.max(
    0,
    notification.mediaIds.length - notification.erroredMediaIds.length,
  );

  return {
    uploadFailures,
    pendingUploadFailures,
    processingFailures,
    cancelled,
    succeededCount,
  };
}
